import React, { useEffect, useState, useCallback } from 'react';
import { brainAPI } from '../lib/api';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import {
  Brain, Loader2, Sparkles, Target, Shield, AlertTriangle,
  TrendingUp, Clock, ArrowRight, CheckCircle, Zap, MessageCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { formatLocal } from '../lib/dateUtils';

const RISK_STYLES = {
  low: { bg: 'bg-emerald-50 dark:bg-emerald-950/40', text: 'text-emerald-700 dark:text-emerald-400', border: 'border-emerald-200 dark:border-emerald-800', icon: CheckCircle },
  medium: { bg: 'bg-amber-50 dark:bg-amber-950/40', text: 'text-amber-700 dark:text-amber-400', border: 'border-amber-200 dark:border-amber-800', icon: AlertTriangle },
  high: { bg: 'bg-orange-50 dark:bg-orange-950/40', text: 'text-orange-700 dark:text-orange-400', border: 'border-orange-200 dark:border-orange-800', icon: AlertTriangle },
  critical: { bg: 'bg-red-50 dark:bg-red-950/40', text: 'text-red-700 dark:text-red-400', border: 'border-red-200 dark:border-red-800', icon: Shield },
};

const PRIORITY_BADGE = {
  high: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  medium: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
  low: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
};

export default function DealCoachingPanel({ dealId, dealStage }) {
  const [coaching, setCoaching] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const fetchCoaching = useCallback(async () => {
    setLoading(true);
    try {
      const res = await brainAPI.getDealCoaching(dealId);
      if (res.data?.exists !== false) setCoaching(res.data);
    } catch { /* no coaching yet */ }
    finally { setLoading(false); }
  }, [dealId]);

  useEffect(() => { if (dealId) fetchCoaching(); }, [dealId, fetchCoaching]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await brainAPI.generateDealCoaching(dealId);
      setCoaching(res.data);
      toast.success('AI coaching generated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate coaching');
    } finally { setGenerating(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">Loading coaching...</span>
      </div>
    );
  }

  const c = coaching?.coaching;

  if (!c) {
    return (
      <div className="text-center py-10 space-y-4" data-testid="coaching-empty">
        <div className="mx-auto w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
          <Brain className="h-7 w-7 text-primary" />
        </div>
        <div>
          <p className="font-medium">No AI coaching yet</p>
          <p className="text-sm text-muted-foreground mt-1">Let the Brain analyze this deal and suggest next-best-actions</p>
        </div>
        <Button onClick={handleGenerate} disabled={generating} data-testid="generate-coaching-btn">
          {generating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Analyzing...</> : <><Sparkles className="h-4 w-4 mr-2" />Get AI Coaching</>}
        </Button>
      </div>
    );
  }

  const risk = c.risk_assessment || {};
  const riskStyle = RISK_STYLES[risk.level] || RISK_STYLES.medium;
  const RiskIcon = riskStyle.icon;
  const winProb = c.win_probability || {};
  const stagnation = c.stagnation_alert || {};
  const actions = c.recommended_actions || [];
  const stageAdvice = c.stage_advice || {};
  const talkingPoints = c.talking_points || [];

  return (
    <div className="space-y-4" data-testid="deal-coaching-panel">
      {/* Coaching Summary */}
      {c.coaching_summary && (
        <Card className="p-4 bg-primary/5 border-primary/20">
          <div className="flex gap-3">
            <Brain className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
            <p className="text-sm leading-relaxed" data-testid="coaching-summary">{c.coaching_summary}</p>
          </div>
        </Card>
      )}

      {/* Risk + Win Probability Row */}
      <div className="grid grid-cols-2 gap-3">
        <Card className={`p-4 ${riskStyle.bg} border ${riskStyle.border}`} data-testid="risk-assessment">
          <div className="flex items-center gap-2 mb-2">
            <RiskIcon className={`h-4 w-4 ${riskStyle.text}`} />
            <span className={`text-xs font-semibold uppercase ${riskStyle.text}`}>Risk: {risk.level}</span>
          </div>
          <p className="text-xs text-muted-foreground">{risk.summary}</p>
          {risk.factors?.length > 0 && (
            <ul className="mt-2 space-y-1">
              {risk.factors.slice(0, 3).map((f, i) => (
                <li key={i} className="text-xs text-muted-foreground flex items-start gap-1">
                  <span className="text-muted-foreground mt-0.5">-</span>{f}
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="p-4" data-testid="win-probability">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="h-4 w-4 text-primary" />
            <span className="text-xs font-semibold uppercase text-muted-foreground">Win Probability</span>
          </div>
          <div className="flex items-end gap-2">
            <span className={`text-3xl font-bold ${(winProb.score || 0) >= 60 ? 'text-emerald-600 dark:text-emerald-400' : (winProb.score || 0) >= 30 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400'}`} data-testid="win-score">
              {winProb.score || 0}%
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">{winProb.reasoning}</p>
        </Card>
      </div>

      {/* Stagnation Alert */}
      {stagnation.is_stagnant && (
        <Card className="p-3 bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800" data-testid="stagnation-alert">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-amber-600" />
            <span className="text-sm font-medium text-amber-700 dark:text-amber-400">Stagnation Alert</span>
            <Badge variant="outline" className="ml-auto text-xs">{stagnation.days_idle || 0} days idle</Badge>
          </div>
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">{stagnation.message}</p>
        </Card>
      )}

      {/* Recommended Actions */}
      {actions.length > 0 && (
        <Card className="p-4">
          <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Zap className="h-4 w-4 text-primary" />Recommended Actions
          </h4>
          <div className="space-y-3">
            {actions.map((action, i) => (
              <div key={i} className="flex gap-3 items-start" data-testid={`coaching-action-${i}`}>
                <div className="mt-1 flex-shrink-0">
                  <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">{i + 1}</div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium">{action.action}</span>
                    <Badge variant="outline" className={`text-[10px] ${PRIORITY_BADGE[action.priority] || ''}`}>{action.priority}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{action.reason}</p>
                  {action.timing && <p className="text-xs text-muted-foreground/70 mt-0.5 flex items-center gap-1"><Clock className="h-3 w-3" />{action.timing}</p>}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Stage Advice */}
      {stageAdvice.current_stage_tips && (
        <Card className="p-4">
          <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
            <Target className="h-4 w-4 text-primary" />Stage Advice ({dealStage})
          </h4>
          <p className="text-sm text-muted-foreground">{stageAdvice.current_stage_tips}</p>
          {stageAdvice.next_milestone && (
            <div className="mt-2 flex items-start gap-2 text-sm">
              <ArrowRight className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
              <span><span className="font-medium">Next milestone:</span> {stageAdvice.next_milestone}</span>
            </div>
          )}
          {stageAdvice.blockers?.length > 0 && (
            <div className="mt-2">
              <span className="text-xs font-medium text-muted-foreground">Watch for:</span>
              <ul className="mt-1 space-y-1">
                {stageAdvice.blockers.map((b, i) => (
                  <li key={i} className="text-xs text-muted-foreground flex items-start gap-1"><AlertTriangle className="h-3 w-3 text-amber-500 flex-shrink-0 mt-0.5" />{b}</li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      )}

      {/* Talking Points */}
      {talkingPoints.length > 0 && (
        <Card className="p-4">
          <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
            <MessageCircle className="h-4 w-4 text-primary" />Talking Points
          </h4>
          <ul className="space-y-2">
            {talkingPoints.map((tp, i) => (
              <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                <CheckCircle className="h-3.5 w-3.5 text-primary flex-shrink-0 mt-0.5" />{tp}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* Refresh */}
      <div className="flex justify-between items-center pt-2">
        <p className="text-xs text-muted-foreground">
          Generated {coaching?.created_at ? formatLocal(coaching.created_at) : ''}
        </p>
        <Button variant="outline" size="sm" onClick={handleGenerate} disabled={generating} data-testid="refresh-coaching-btn">
          {generating ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Sparkles className="h-3 w-3 mr-1" />}
          Refresh
        </Button>
      </div>
    </div>
  );
}
